/**
 * CaptionsContent — Content Studio Captions tab (PLAT-9626).
 *
 * Read-only view of the story's translated caption track. Reuses the same
 * `<Transcript>` renderer Creator Studio uses for captions; the
 * `forceCaptionsView` prop flips the captions branch (PLAT-9621) on
 * without touching the page-global `videoCreator.showSubtitles` toggle
 * (which would also affect Creator Studio).
 *
 * Word-level translation edits made on any surface (Creator Studio
 * Transcript, Content Studio Transcript & Speakers, the redline view)
 * propagate here automatically through the singleton `TranslationStore`'s
 * MobX observer chain — same pattern proven by PLAT-9624. No new
 * propagation mechanism is added in this ticket.
 */

import { trackTranslationCaptionsViewed } from '@src/analytics/EventTracker';
import SimpleLoadingSpinner from '@src/components/SimpleLoadingSpinner';
import { Transcript } from '@src/components/transcript/Transcript';
import { TranslationGenerateModal } from '@src/components/transcript/TranslationGenerateModal';
import { TranslationToggle } from '@src/components/transcript/TranslationToggle';
import { useTranslationGenerateModalController } from '@src/components/transcript/useTranslationGenerateModalController';
import { useLanguageRemovalFallback } from '@src/hooks/useLanguageRemovalFallback';
import { useTranscriptLoadRekick } from '@src/hooks/useTranscriptLoadRekick';
import { useTranslationStoreHydration } from '@src/hooks/useTranslationStoreHydration';
import {
  useKaraokeStore,
  useStore,
  useTranscription,
  useUserIdentity,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { isOriginalLang } from '@src/utility/captionLanguage';
import { storyHasTranscript } from '@src/utility/transcriptLoadRetry';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

const CaptionsContent: React.FC = observer(() => {
  const transcription = useTranscription();
  const karaokeStore = useKaraokeStore();
  const rootStore = useStore();
  const videoCreator = useVideoCreatorStore();
  const userIdentity = useUserIdentity();
  const { contentStudioEnableTranslatedUndoRedo } = useFlagsCombination(
    userIdentity.currentRole,
  );

  // Per-view language selection — null = Original. Owned locally so other
  // surfaces (Creator Studio, T&S tab, redline) can hold their own
  // selection independently per the per-view-independence epic invariant.
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
  const generateModal = useTranslationGenerateModalController();

  const supportedLanguages = useTranslationStoreHydration();

  // PLAT-9700: fall back to Original when the active translation is removed.
  useLanguageRemovalFallback(selectedLanguage, () => setSelectedLanguage(null));

  // Count of karaoke lines PRODUCED for the selected language. Drives the
  // empty-state messaging plus the analytics payload. `!= null`, not
  // truthiness: a row the reviewer emptied ("Remove Translation" over all of
  // it) stores `''` and is still a produced row.
  const translatedClipCount = useMemo(() => {
    if (!selectedLanguage) return 0;
    return karaokeStore.karaokeLines.filter(line => line.translatedText != null)
      .length;
  }, [karaokeStore.karaokeLines, selectedLanguage]);

  // Fire the viewed event whenever the language selection settles on a
  // non-Original value. Re-fires on language switch (each language is its
  // own viewing session). Original (null) is intentionally silent — the
  // tab only adds product value when a translation is selected.
  useEffect(() => {
    if (!selectedLanguage) return;
    trackTranslationCaptionsViewed({
      story_id: rootStore.storyId ?? undefined,
      language: selectedLanguage,
      clip_count: translatedClipCount,
    });
  }, [selectedLanguage, translatedClipCount, rootStore.storyId]);

  const isTranscriptionReady = transcription?.isTranscriptionReady;
  const isTranscriptionLoading = transcription?.isTranscriptionLoading;

  // PLAT-11697: same retry as TranscriptSpeakersContent.
  useTranscriptLoadRekick({
    storyId: videoCreator.story?.id ?? null,
    hasTranscript: storyHasTranscript(videoCreator.story),
    isReady: !!isTranscriptionReady,
    isLoading: !!isTranscriptionLoading,
    load: () => videoCreator.loadTranscriptIfNeeded(),
  });

  if (isTranscriptionLoading) {
    return (
      <LoadingContainer>
        <SimpleLoadingSpinner text="Loading transcript data…" />
      </LoadingContainer>
    );
  }

  if (!isTranscriptionReady) {
    return (
      <LoadingContainer>
        <SimpleLoadingSpinner text="Preparing transcript data…" />
      </LoadingContainer>
    );
  }

  // No karaoke produced anywhere yet — captions can't render. Distinct
  // from "no translated captions in this language", which the captions
  // branch in Transcript.tsx falls back on the original-language karaoke
  // view to handle. We don't try to coach the user to produce karaoke
  // here; that flow lives in Creator Studio.
  if (!karaokeStore.hasKaraokeLines) {
    return (
      <EmptyContainer>
        <EmptyTitle>No captions produced yet</EmptyTitle>
        <EmptyBody>
          Open Creator Studio to generate captions for this story. Once
          produced, this tab will show the translated caption track for any
          language a translation has been generated in.
        </EmptyBody>
      </EmptyContainer>
    );
  }

  return (
    <Container>
      <CaptionsPane>
        <Transcript
          surface="content_studio_transcript_tab"
          hideHeader={false}
          // Hidden while a translated language is on screen: the undo stack
          // doesn't cover translated edits, so the buttons only look like they
          // work (flag defaults on).
          showUndoRedo={
            contentStudioEnableTranslatedUndoRedo ||
            isOriginalLang(selectedLanguage)
          }
          hideAddPhoto
          hideCutText
          hideRemoveText
          hideReplace
          hideSaveAsClip
          hideCaptions
          useOriginalTranscript
          forceCaptionsView
          language={selectedLanguage}
          headerExtras={
            <TranslationToggle
              selectedLanguage={selectedLanguage}
              onLanguageChange={setSelectedLanguage}
              onRequestGenerate={generateModal.onRequestGenerate}
              generateDisabled={generateModal.isOpen}
              supportedLanguages={supportedLanguages}
              surface="content-studio-captions"
            />
          }
        />
      </CaptionsPane>
      {generateModal.isOpen && (
        <TranslationGenerateModal
          {...generateModal.modalProps}
          onSuccess={language => {
            setSelectedLanguage(language);
            generateModal.close();
          }}
          supportedLanguages={supportedLanguages}
        />
      )}
    </Container>
  );
});

const Container = styled.div`
  position: relative;
  width: 100%;
  height: calc(100vh - 70px);
  margin-top: -15px;
`;

const CaptionsPane = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;

  /* Mirror TranscriptSpeakersContent's indent fix — Transcript's Main uses
     width:100% + margin-left:32px which overflows by 32px. */
  & > #transcript-container {
    margin-left: 0;
    padding-left: 32px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  gap: 20px;
`;

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  gap: 12px;
  padding: 24px;
  text-align: center;
`;

const EmptyTitle = styled.div`
  font-family: var(--font-primary);
  font-size: var(--text-md);
  font-weight: var(--font-weight-medium);
  color: var(--foreground);
`;

const EmptyBody = styled.div`
  max-width: 480px;
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  color: var(--text-secondary);
  line-height: var(--leading-text-sm);
`;

export default CaptionsContent;
